import { chartTypeOptions } from 'src/pages/Solutions/SolutionAssetHierarchy/constant';
import {
  HMIAccess,
  HMIActionBehavior,
  HMIChartType,
  HMIDataType,
  HMIFieldKind,
  HMIFieldOption,
  HMIRestApiMethod,
  HMIRuleOperator,
  HMISourceKind,
} from '../../types';

export const FIELD_KIND_OPTIONS: { value: HMIFieldKind; label: string }[] = [
  { value: 'indicator', label: 'Indicator (read-only display)' },
  { value: 'control', label: 'Control (operator input)' },
  { value: 'action', label: 'Action (command button)' },
  { value: 'chart', label: 'Chart / graph' },
  { value: 'image', label: 'Image with overlay (live values pinned on a picture)' },
];

export const DATATYPE_OPTIONS: { value: HMIDataType; label: string }[] = [
  { value: 'bool', label: 'Boolean' },
  { value: 'i32', label: 'Integer (i32)' },
  { value: 'f32', label: 'Float (f32)' },
  { value: 'f64', label: 'Double (f64)' },
  { value: 'string', label: 'String' },
  { value: 'enum', label: 'Enum (options list)' },
  { value: 'array', label: 'Array (grid)' },
];

export const ACCESS_OPTIONS: { value: HMIAccess; label: string }[] = [
  { value: 'read', label: 'Read-only' },
  { value: 'write', label: 'Write-only' },
  { value: 'read_write', label: 'Read / write' },
  { value: 'derived', label: 'Derived (computed)' },
];

export const SOURCE_KIND_OPTIONS: { value: HMISourceKind; label: string }[] = [
  { value: 'live', label: 'Live tag (polled from edge)' },
  { value: 'derived', label: 'Derived (computed in UI)' },
  { value: 'config', label: 'Config (recipe value)' },
];

export const ARRAY_OF_OPTIONS = ['i32', 'f32', 'f64', 'bool'] as const;

/**
 * Built-in behaviors a `kind: "action"` button can run, picked visually
 * instead of needing a code change (see `HMIActionBehavior`). "Custom /
 * simulated" is the historical default: click -> log entry, no side effect.
 */
export const ACTION_BEHAVIOR_OPTIONS: { value: HMIActionBehavior; label: string }[] = [
  { value: 'none', label: 'Custom / simulated only (no built-in effect)' },
  { value: 'load_process', label: 'Load process file (opens a file picker, fills this screen)' },
  { value: 'save_process', label: 'Save process file (saves this screen\u2019s current values)' },
  { value: 'delete_process', label: 'Delete process file (opens a file picker to remove one)' },
  { value: 'clear_process', label: 'Clear fields (resets this screen back to defaults)' },
  { value: 'rest_api', label: 'Call REST API (configure method/URL/headers/body)' },
];

/** HTTP methods offered for a `rest_api` action button. */
export const REST_API_METHOD_OPTIONS: { value: HMIRestApiMethod; label: string }[] = [
  { value: 'GET', label: 'GET' },
  { value: 'POST', label: 'POST' },
  { value: 'PUT', label: 'PUT' },
  { value: 'PATCH', label: 'PATCH' },
  { value: 'DELETE', label: 'DELETE' },
];

const NUMERIC_DATATYPES: HMIDataType[] = ['i32', 'f32', 'f64'];

/** Which comparison operators make sense for a field's data type, in a dependency rule clause. */
export function operatorOptionsFor(datatype: HMIDataType): { value: HMIRuleOperator; label: string }[] {
  const equality: { value: HMIRuleOperator; label: string }[] = [
    { value: '==', label: 'equals' },
    { value: '!=', label: 'does not equal' },
  ];
  if (!NUMERIC_DATATYPES.includes(datatype)) return equality;
  return [
    ...equality,
    { value: '>', label: 'is greater than' },
    { value: '>=', label: 'is at least' },
    { value: '<', label: 'is less than' },
    { value: '<=', label: 'is at most' },
  ];
}

/** A sensible default literal to compare a field of this data type against. */
export function defaultRuleValueFor(datatype: HMIDataType, options?: HMIFieldOption[]): string | number | boolean {
  if (datatype === 'bool') return true;
  if (datatype === 'enum') return options?.[0]?.value ?? '';
  if (NUMERIC_DATATYPES.includes(datatype)) return 0;
  return '';
}

/**
 * Chart type picker for `kind: "chart"` fields - reuses the same chart
 * vocabulary as the dashboard "Add widget" flow (`chartTypeOptions` in
 * `src/pages/Solutions/SolutionAssetHierarchy/constant.ts`), trimmed to the
 * types that make sense for a single live HMI tag (time-series/snapshot),
 * plus `gauge` for a bounded single value like a pressure or temperature.
 */
export const HMI_CHART_TYPE_OPTIONS: { value: HMIChartType; label: string }[] = [
  ...chartTypeOptions.filter((o) => ['line', 'bar', 'area'].includes(o.value)),
  { value: 'gauge', label: 'Gauge' },
] as { value: HMIChartType; label: string }[];
